import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { db } from '../firebase/config';
import {
  collection, getDocs, getDoc, addDoc, deleteDoc, doc, updateDoc, setDoc,
  serverTimestamp, onSnapshot, orderBy, query, where, arrayUnion,
} from 'firebase/firestore';
import {
  Box, Typography, Paper, Button, IconButton, Chip, CircularProgress,
  TextField, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Badge, Divider, Alert, InputAdornment, MenuItem, Select, FormControl,
  InputLabel, Switch, FormControlLabel, Snackbar, Avatar, Autocomplete,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import GavelIcon from '@mui/icons-material/Gavel';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ShareIcon from '@mui/icons-material/Share';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { QRCodeSVG } from 'qrcode.react';
import LagbasLogo from '../components/LagbasLogo';
import { deptLabel } from '../utils/depts';
import { authFetch } from '../utils/authFetch';

const SOURCES = [
  {
    id: 'upgraded',
    name: 'Upgraded People',
    url: 'https://upgraded.se',
    logo: '/upgraded_logo.svg',
    desc: 'Konsultförfrågningar från offentlig och privat sektor',
  },
];

const STATUS_COLORS = {
  Ongoing: { bg: '#e8f5e9', color: '#2E7D32', label: 'Pågående' },
  Closed:  { bg: '#fce4ec', color: '#c62828', label: 'Stängd' },
  Draft:   { bg: '#fff8e1', color: '#e65100', label: 'Utkast' },
};

function stripHtml(html) {
  return html
    ?.replace(/<\/?(p|div|br|li|h[1-6])[^>]*>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n[ \t]*/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim() || '';
}

function daysLeft(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / 86400000);
}

function formatDate(dateStr) {
  if (!dateStr) return null;
  if (dateStr === 'ASAP' || dateStr === 'Snarast') return 'Snarast';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('sv-SE', { day: 'numeric', month: 'long', year: 'numeric' });
}

function MetaRow({ label, value, icon }) {
  if (!value) return null;
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', py: 0.5 }}>
      {icon && React.cloneElement(icon, { sx: { fontSize: 14, color: '#999', mt: '3px', flexShrink: 0 } })}
      <Box>
        <Typography variant="caption" sx={{ color: '#aaa', display: 'block', lineHeight: 1.2, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.4 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.4 }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

function matchesAlert(item, rule) {
  const text = [item.title, item.role, item.competenceArea, item.missionInfo,
    ...(item.requirements || []).map(r => r.name)].join(' ').toLowerCase();
  const kwMatch = !rule.keywords?.length ||
    rule.keywords.some(kw => text.includes(kw.toLowerCase()));
  const areaMatch = !rule.competenceIds?.length ||
    rule.competenceIds.includes(item.competenceId);
  const cityMatch = !rule.cities?.length ||
    rule.cities.some(c => item.city?.toLowerCase().includes(c.toLowerCase()));
  return kwMatch && areaMatch && cityMatch;
}

export default function Upphandlingar() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser, userProfile } = useAuth();
  const commentUnsubRef = useRef(null);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastFetched, setLastFetched] = useState(null);

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('Ongoing');
  const [filterArea, setFilterArea] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [filterBookmarked, setFilterBookmarked] = useState(false);

  const [alerts, setAlerts] = useState([]);
  const [alertDialog, setAlertDialog] = useState(false);
  const [alertForm, setAlertForm] = useState({ name: '', keywords: '', competenceIds: [], departmentIds: [], cities: '', active: true });
  const [editAlertId, setEditAlertId] = useState(null);
  const [departments, setDepartments] = useState([]);

  const [bookmarks, setBookmarks] = useState({}); // { [itemId]: true }
  const [dismissed, setDismissed] = useState({}); // { [itemId]: true }
  const [hideDismissed, setHideDismissed] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [creatingTender, setCreatingTender] = useState(null);
  const [snackbar, setSnackbar] = useState('');

  // Dela + kommentarer
  const [shareDialog, setShareDialog] = useState(null); // { item, savedId, shareNote }
  const [savedComments, setSavedComments] = useState([]);
  const [newCommentText, setNewCommentText] = useState('');
  const [shareNote, setShareNote] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [savingShare, setSavingShare] = useState(false);

  // Bjud in kollegor
  const [allUsers, setAllUsers] = useState([]);
  const [selectedInvitees, setSelectedInvitees] = useState([]);
  const [sendingInvite, setSendingInvite] = useState(false);

  useEffect(() => {
    loadDepts();
    loadAlerts();
    loadBookmarks();
    loadDismissed();
    loadUsers();
    fetchItems();
  }, []); // eslint-disable-line

  async function loadDismissed() {
    const snap = await getDocs(collection(db, 'upphandlingarDismissed'));
    const map = {};
    snap.docs.forEach(d => { map[d.id] = true; });
    setDismissed(map);
  }

  async function toggleDismiss(item) {
    const isDismissed = !!dismissed[item.id];
    if (isDismissed) {
      await deleteDoc(doc(db, 'upphandlingarDismissed', item.id));
      setDismissed(prev => { const n = { ...prev }; delete n[item.id]; return n; });
    } else {
      await setDoc(doc(db, 'upphandlingarDismissed', item.id), { title: item.title, savedAt: new Date().toISOString() });
      setDismissed(prev => ({ ...prev, [item.id]: true }));
      if (expandedId === item.id) setExpandedId(null);
    }
  }

  async function loadUsers() {
    const snap = await getDocs(collection(db, 'users'));
    setAllUsers(
      snap.docs
        .map(d => ({ uid: d.id, name: d.data().name || d.data().displayName || '', email: d.data().email || '' }))
        .filter(u => u.email && u.uid !== currentUser?.uid)
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'sv'))
    );
  }

  async function loadDepts() {
    const snap = await getDocs(collection(db, 'departments'));
    setDepartments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }

  async function loadAlerts() {
    const snap = await getDocs(collection(db, 'upphandlingarAlerts'));
    setAlerts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }

  async function loadBookmarks() {
    const snap = await getDocs(collection(db, 'upphandlingarBookmarks'));
    const map = {};
    snap.docs.forEach(d => { map[d.id] = d.data(); });
    setBookmarks(map);
  }

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await authFetch('/.netlify/functions/upgraded-inquiries');
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch (_) {
        setError(`Kunde inte tolka svar (HTTP ${res.status})`); return;
      }
      if (!res.ok) { setError(data.error || `HTTP ${res.status}`); return; }
      setItems(data.items || []);
      setLastFetched(new Date());
    } catch (e) {
      setError('Nätverksfel: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  async function toggleBookmark(item) {
    const isBookmarked = !!bookmarks[item.id];
    if (isBookmarked) {
      await deleteDoc(doc(db, 'upphandlingarBookmarks', item.id));
      setBookmarks(prev => { const n = { ...prev }; delete n[item.id]; return n; });
    } else {
      const data = { title: item.title, role: item.role, source: item.source,
        savedAt: new Date().toISOString() };
      await setDoc(doc(db, 'upphandlingarBookmarks', item.id), data);
      setBookmarks(prev => ({ ...prev, [item.id]: data }));
    }
  }

  // ── Dela & kommentarer ──────────────────────────────────────────────────────

  async function openShare(item) {
    setSavingShare(true);
    try {
      const q = query(collection(db, 'upphandlingarSaved'), where('sourceId', '==', item.id));
      const snap = await getDocs(q);
      let savedId;
      if (!snap.empty) {
        savedId = snap.docs[0].id;
      } else {
        const ref = await addDoc(collection(db, 'upphandlingarSaved'), {
          sourceId:      item.id,
          title:         item.title         || '',
          role:          item.role          || '',
          competenceArea: item.competenceArea || '',
          scope:         item.scope         || '',
          missionInfo:   item.missionInfo   || '',
          requirements:  item.requirements  || [],
          city:          item.city          || '',
          country:       item.country       || '',
          startDate:     item.startDate     || '',
          endDate:       item.endDate       || '',
          lastResponseDate: item.lastResponseDate || '',
          referenceNumber: item.referenceNumber  || '',
          contact:       item.contact       || {},
          url:           item.url           || '',
          status:        item.status        || '',
          sharedBy:      currentUser?.uid   || '',
          sharedByName:  userProfile?.name  || currentUser?.displayName || '',
          sharedAt:      serverTimestamp(),
          participants:  [],
        });
        savedId = ref.id;
      }
      startCommentListener(savedId);
      const existingNote = snap.empty ? '' : (snap.docs[0].data().shareNote || '');
      setShareNote(existingNote);
      setShareDialog({ item, savedId, shareNote: existingNote });
    } catch (e) {
      setSnackbar('Kunde inte dela: ' + e.message);
    } finally {
      setSavingShare(false);
    }
  }

  function startCommentListener(savedId) {
    if (commentUnsubRef.current) commentUnsubRef.current();
    const unsub = onSnapshot(
      query(collection(db, 'upphandlingarSaved', savedId, 'comments'), orderBy('createdAt', 'asc')),
      snap => setSavedComments(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    );
    commentUnsubRef.current = unsub;
  }

  function closeShare() {
    if (commentUnsubRef.current) { commentUnsubRef.current(); commentUnsubRef.current = null; }
    setShareDialog(null);
    setSavedComments([]);
    setNewCommentText('');
    setShareNote('');
    setSelectedInvitees([]);
  }

  async function sendInvite() {
    if (!selectedInvitees.length || !shareDialog) return;
    setSendingInvite(true);
    const { savedId, item } = shareDialog;
    const senderName = userProfile?.name || currentUser?.displayName || currentUser?.email || 'Kollega';
    const shareUrl   = `${window.location.origin}/upphandlingar?share=${savedId}`;
    try {
      // Spara som deltagare i Firestore
      for (const u of selectedInvitees) {
        await updateDoc(doc(db, 'upphandlingarSaved', savedId), {
          participants: arrayUnion({ uid: u.uid, name: u.name, email: u.email }),
        });
      }
      // Skicka e-post via Resend
      await authFetch('/.netlify/functions/upphandling-invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: item.title,
          senderName,
          shareUrl,
          recipients: selectedInvitees.map(u => ({ name: u.name, email: u.email })),
          shareNote: shareNote || shareDialog.shareNote || '',
          summary: {
            role:          item.role          || '',
            competenceArea: item.competenceArea || '',
            location:      [item.city, item.country].filter(Boolean).join(', '),
            scope:         item.scope         || '',
            startDate:     formatDate(item.startDate),
            deadline:      formatDate(item.lastResponseDate),
          },
        }),
      });
      setSelectedInvitees([]);
      setSnackbar(`Inbjudan skickad till ${selectedInvitees.map(u => u.name || u.email).join(', ')}`);
    } catch (e) {
      setSnackbar('Kunde inte skicka inbjudan: ' + e.message);
    } finally {
      setSendingInvite(false);
    }
  }

  async function saveShareNote(savedId, note) {
    try {
      await updateDoc(doc(db, 'upphandlingarSaved', savedId), { shareNote: note });
      setShareDialog(prev => prev ? { ...prev, shareNote: note } : prev);
    } catch (_) {}
  }

  async function submitComment() {
    if (!newCommentText.trim() || !shareDialog) return;
    setSendingComment(true);
    const text       = newCommentText.trim();
    const authorName = userProfile?.name || currentUser?.displayName || currentUser?.email || 'Okänd';
    const authorId   = currentUser?.uid  || '';
    const authorEmail = userProfile?.email || currentUser?.email || '';
    const { savedId, item } = shareDialog;
    try {
      await addDoc(collection(db, 'upphandlingarSaved', savedId, 'comments'), {
        text, authorId, authorName,
        authorInitial: authorName.charAt(0).toUpperCase(),
        createdAt: serverTimestamp(),
      });
      // Lägg till som deltagare
      await updateDoc(doc(db, 'upphandlingarSaved', savedId), {
        participants: arrayUnion({ uid: authorId, name: authorName, email: authorEmail }),
      });
      setNewCommentText('');
      // Skicka e-postavisering till övriga deltagare
      const otherParticipants = savedComments
        .map(c => ({ uid: c.authorId, name: c.authorName, email: '' }))
        .filter((p, i, arr) => p.uid !== authorId && arr.findIndex(x => x.uid === p.uid) === i);
      if (otherParticipants.length > 0) {
        const shareUrl = `${window.location.origin}/upphandlingar?share=${savedId}`;
        authFetch('/.netlify/functions/upphandling-comment', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: item.title,
            commenterName: authorName,
            commentText: text,
            participants: otherParticipants,
            shareUrl,
            shareNote: shareDialog.shareNote || '',
            summary: {
              role:          item.role          || '',
              competenceArea: item.competenceArea || '',
              location:      [item.city, item.country].filter(Boolean).join(', '),
              scope:         item.scope         || '',
              startDate:     formatDate(item.startDate),
              deadline:      formatDate(item.lastResponseDate),
            },
          }),
        }).catch(() => {});
      }
    } catch (e) {
      setSnackbar('Kunde inte skicka kommentar: ' + e.message);
    } finally {
      setSendingComment(false);
    }
  }

  // Öppna dela-dialog automatiskt vid ?share=xxx i URL
  useEffect(() => {
    const shareId = searchParams.get('share');
    if (!shareId) return;
    (async () => {
      const snap = await getDoc(doc(db, 'upphandlingarSaved', shareId));
      if (snap.exists()) {
        startCommentListener(shareId);
        setShareDialog({ item: snap.data(), savedId: shareId });
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Skapa anbud ──────────────────────────────────────────────────────────────

  async function createTender(item) {
    setCreatingTender(item.id);
    try {
      const mandatory  = item.requirements?.filter(r => r.type === 'Mandatory')  || [];
      const shouldHave = item.requirements?.filter(r => r.type === 'ShouldHave') || [];
      const ref = await addDoc(collection(db, 'tenders'), {
        name:              item.title || 'Ny upphandling',
        client:            'Upgraded People',
        briefingText:      stripHtml(item.missionInfo),
        portalUrl:         item.url || '',
        deadline:          item.lastResponseDate || '',
        mustRequirements:  mandatory.map(r => r.name),
        shouldRequirements: shouldHave.map(r => r.name),
        mustRequirementsChecked:  [],
        shouldRequirementsChecked: [],
        status:            'draft',
        source:            'upgraded',
        sourceItemId:      item.id,
        referenceNumber:   item.referenceNumber || '',
        role:              item.role || '',
        competenceArea:    item.competenceArea || '',
        scope:             item.scope || '',
        city:              item.city || '',
        country:           item.country || '',
        startDate:         item.startDate || '',
        endDate:           item.endDate || '',
        contact:           item.contact || {},
        createdAt:         serverTimestamp(),
        updatedAt:         serverTimestamp(),
      });
      navigate(`/tenders?open=${ref.id}`);
    } catch (e) {
      setSnackbar('Kunde inte skapa anbud: ' + e.message);
    } finally {
      setCreatingTender(null);
    }
  }

  function copyShareText(item) {
    const mandatory  = item.requirements?.filter(r => r.type === 'Mandatory')  || [];
    const lines = [
      `📋 ${item.title}`,
      item.role              && `Roll: ${item.role}`,
      item.competenceArea    && `Kompetensområde: ${item.competenceArea}`,
      item.scope             && `Omfattning: ${item.scope}`,
      item.workPlace         && `Plats: ${item.workPlace}`,
      item.startDate         && `Start: ${formatDate(item.startDate)}`,
      item.endDate           && `Slutdatum: ${formatDate(item.endDate)}`,
      item.lastResponseDate  && `Ansök senast: ${formatDate(item.lastResponseDate)}`,
      item.referenceNumber   && `Ref: ${item.referenceNumber}`,
      mandatory.length > 0   && `\nSkakrav:\n${mandatory.map(r => `• ${r.name}`).join('\n')}`,
      item.url               && `\n${item.url}`,
    ].filter(Boolean).join('\n');
    navigator.clipboard.writeText(lines).then(() => setSnackbar('Kopierat till urklipp!'));
  }

  const areas = [...new Set(items.map(i => i.competenceArea).filter(Boolean))];
  const cities = [...new Set(items.map(i => i.city).filter(Boolean))];

  const deadlineMs = d => d ? new Date(d).getTime() : Infinity;

  const filtered = items.filter(item => {
    if (filterStatus && item.status !== filterStatus) return false;
    if (filterArea && item.competenceArea !== filterArea) return false;
    if (filterCity && !item.city?.toLowerCase().includes(filterCity.toLowerCase())) return false;
    if (filterBookmarked && !bookmarks[item.id]) return false;
    if (hideDismissed && dismissed[item.id]) return false;
    if (search) {
      const q = search.toLowerCase();
      const text = [item.title, item.role, item.competenceArea, item.city,
        stripHtml(item.missionInfo)].join(' ').toLowerCase();
      if (!text.includes(q)) return false;
    }
    return true;
  }).sort((a, b) => {
    const aStarred = !!bookmarks[a.id];
    const bStarred = !!bookmarks[b.id];
    const aDis = !!dismissed[a.id];
    const bDis = !!dismissed[b.id];
    if (aStarred !== bStarred) return aStarred ? -1 : 1;
    if (aDis !== bDis) return aDis ? 1 : -1;
    return deadlineMs(a.lastResponseDate) - deadlineMs(b.lastResponseDate);
  });

  const activeAlerts = alerts.filter(a => a.active !== false);
  function getMatchingAlerts(item) {
    return activeAlerts.filter(a => matchesAlert(item, a));
  }
  const matchCount = items.filter(i => getMatchingAlerts(i).length > 0).length;
  const bookmarkCount = Object.keys(bookmarks).length;
  const dismissCount  = Object.keys(dismissed).length;

  async function saveAlert() {
    const data = {
      name: alertForm.name,
      keywords: alertForm.keywords.split(',').map(k => k.trim()).filter(Boolean),
      competenceIds: alertForm.competenceIds,
      departmentIds: alertForm.departmentIds,
      cities: alertForm.cities.split(',').map(c => c.trim()).filter(Boolean),
      active: alertForm.active,
    };
    if (editAlertId) {
      await updateDoc(doc(db, 'upphandlingarAlerts', editAlertId), data);
      setAlerts(prev => prev.map(a => a.id === editAlertId ? { id: editAlertId, ...data } : a));
    } else {
      const ref = await addDoc(collection(db, 'upphandlingarAlerts'), data);
      setAlerts(prev => [...prev, { id: ref.id, ...data }]);
    }
    setAlertDialog(false);
    setEditAlertId(null);
  }

  function openNewAlert() {
    setAlertForm({ name: '', keywords: '', competenceIds: [], departmentIds: [], cities: '', active: true });
    setEditAlertId(null);
    setAlertDialog(true);
  }

  function openEditAlert(a) {
    setAlertForm({
      name: a.name || '',
      keywords: (a.keywords || []).join(', '),
      competenceIds: a.competenceIds || [],
      departmentIds: a.departmentIds || [],
      cities: (a.cities || []).join(', '),
      active: a.active !== false,
    });
    setEditAlertId(a.id);
    setAlertDialog(true);
  }

  async function deleteAlert(id) {
    await deleteDoc(doc(db, 'upphandlingarAlerts', id));
    setAlerts(prev => prev.filter(a => a.id !== id));
  }

  async function toggleAlert(a) {
    await updateDoc(doc(db, 'upphandlingarAlerts', a.id), { active: !a.active });
    setAlerts(prev => prev.map(x => x.id === a.id ? { ...x, active: !x.active } : x));
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      {/* Toolbar */}
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LagbasLogo />
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
          <IconButton color="inherit" size="small" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">Upphandlingar API</Typography>
            <Typography variant="caption" sx={{ color: '#aaa' }}>
              {lastFetched
                ? `Uppdaterad ${lastFetched.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}`
                : 'Hämtar...'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title="Hantera larm">
            <IconButton color="inherit" onClick={() => setAlertDialog('list')}>
              <Badge badgeContent={matchCount || null} color="warning"
                sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem' } }}>
                {activeAlerts.length > 0
                  ? <NotificationsActiveIcon sx={{ color: '#FF6B00' }} />
                  : <NotificationsNoneIcon />}
              </Badge>
            </IconButton>
          </Tooltip>
          <Tooltip title="Uppdatera">
            <IconButton color="inherit" onClick={fetchItems} disabled={loading}>
              {loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1100, mx: 'auto', p: 3 }}>

        {/* ── Anslutna tjänster ── */}
        <Paper sx={{ p: 2.5, mb: 3, display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
          <Box sx={{ minWidth: 200 }}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.3 }}>
              Anslutna tjänster
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5 }}>
              Konsultförfrågningar hämtas automatiskt från anslutna portaler.
            </Typography>
            <Typography variant="caption" sx={{ color: '#bbb', fontStyle: 'italic' }}>
              Fler tjänster tillkommer löpande.
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
          {SOURCES.map(src => (
            <Tooltip key={src.id} title={src.desc}>
              <Box
                component="a" href={src.url} target="_blank" rel="noopener noreferrer"
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, textDecoration: 'none',
                  borderRadius: 2, px: 1.5, py: 1,
                  border: '1px solid #e0e0e0', bgcolor: 'white',
                  transition: 'box-shadow 0.15s',
                  '&:hover': { boxShadow: 2 } }}>
                <Box component="img" src={src.logo} alt={src.name}
                  sx={{ height: 28, width: 'auto', borderRadius: 1 }} />
                <Box>
                  <Typography variant="caption" fontWeight="bold" display="block">{src.name}</Typography>
                  <Chip size="small" label={`${items.filter(i => i.sourceId === src.id).length} ärenden`}
                    sx={{ fontSize: '0.65rem', height: 18 }} />
                </Box>
                <OpenInNewIcon sx={{ fontSize: 14, color: '#aaa', ml: 0.5 }} />
              </Box>
            </Tooltip>
          ))}
          {/* Kommande tjänster */}
          {[
            { name: 'Visma TendSign', desc: 'Offentliga upphandlingar via TendSign' },
            { name: 'Mercell', desc: 'Nordisk upphandlingsplattform' },
          ].map(upcoming => (
            <Tooltip key={upcoming.name} title={`Planerad integration: ${upcoming.desc}`}>
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                borderRadius: 2, px: 1.5, py: 1,
                border: '1px dashed #ddd', bgcolor: '#fafafa', opacity: 0.6,
              }}>
                <Box>
                  <Typography variant="caption" fontWeight="bold" display="block" sx={{ color: '#aaa' }}>
                    {upcoming.name}
                  </Typography>
                  <Chip size="small" label="Kommer snart"
                    sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#f0f0f0', color: '#bbb' }} />
                </Box>
              </Box>
            </Tooltip>
          ))}
        </Paper>

        {/* ── Filter + sökning ── */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small" placeholder="Sök titel, roll, kompetens..."
            value={search} onChange={e => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 220 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment> }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value)}>
              <MenuItem value="">Alla</MenuItem>
              <MenuItem value="Ongoing">Pågående</MenuItem>
              <MenuItem value="Closed">Stängda</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Kompetensområde</InputLabel>
            <Select value={filterArea} label="Kompetensområde" onChange={e => setFilterArea(e.target.value)}>
              <MenuItem value="">Alla</MenuItem>
              {areas.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Ort</InputLabel>
            <Select value={filterCity} label="Ort" onChange={e => setFilterCity(e.target.value)}>
              <MenuItem value="">Alla</MenuItem>
              {cities.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>
          <Tooltip title={filterBookmarked ? 'Visa alla' : 'Visa bara intressanta'}>
            <IconButton
              onClick={() => setFilterBookmarked(v => !v)}
              sx={{ color: filterBookmarked ? '#FF6B00' : 'text.secondary' }}>
              <Badge badgeContent={bookmarkCount || null} color="warning"
                sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem' } }}>
                {filterBookmarked ? <StarIcon /> : <StarBorderIcon />}
              </Badge>
            </IconButton>
          </Tooltip>
          {dismissCount > 0 && (
            <Tooltip title={hideDismissed ? 'Visa även ointressanta' : 'Dölj ointressanta'}>
              <Chip
                size="small"
                label={hideDismissed ? `${dismissCount} dolda` : `${dismissCount} ointressanta`}
                onClick={() => setHideDismissed(v => !v)}
                sx={{
                  fontSize: '0.7rem', height: 26, cursor: 'pointer',
                  bgcolor: hideDismissed ? '#616161' : '#f0f0f0',
                  color: hideDismissed ? '#fff' : '#888',
                  '&:hover': { bgcolor: hideDismissed ? '#424242' : '#e0e0e0' },
                }}
              />
            </Tooltip>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {filtered.length} av {items.length}
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {matchCount > 0 && (
          <Alert severity="warning" icon={<NotificationsActiveIcon />} sx={{ mb: 2 }}>
            <strong>{matchCount} ärende{matchCount !== 1 ? 'n' : ''}</strong> matchar dina larmregler.
          </Alert>
        )}

        {/* ── Kort ── */}
        {loading && items.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">Inga ärenden matchar filtret.</Typography>
          </Paper>
        ) : (
          filtered.map(item => {
            const st = STATUS_COLORS[item.status] || {};
            const days = daysLeft(item.lastResponseDate);
            const expanded = expandedId === item.id;
            const matching = getMatchingAlerts(item);
            const isBookmarked = !!bookmarks[item.id];
            const isDismissed  = !!dismissed[item.id];
            const mandatory = item.requirements?.filter(r => r.type === 'Mandatory') || [];
            const shouldHave = item.requirements?.filter(r => r.type === 'ShouldHave') || [];

            return (
              <Paper key={item.id} sx={{
                mb: 2, overflow: 'hidden',
                opacity: isDismissed ? 0.45 : 1,
                filter: isDismissed ? 'grayscale(60%)' : 'none',
                border: isDismissed
                  ? '1px solid #ddd'
                  : isBookmarked
                    ? '2px solid #FF6B00'
                    : matching.length > 0
                      ? '2px solid #1565C0'
                      : '1px solid #e0e0e0',
                transition: 'box-shadow 0.15s, opacity 0.2s',
                '&:hover': { boxShadow: isDismissed ? 0 : 3 },
              }}>
                {/* Header */}
                <Box sx={{ p: 2.5, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  {/* Bokmärkesknapp */}
                  <Tooltip title={isBookmarked ? 'Ta bort som intressant' : 'Markera som intressant'}>
                    <IconButton
                      size="small"
                      onClick={() => toggleBookmark(item)}
                      sx={{ mt: 0.3, flexShrink: 0,
                        color: isBookmarked ? '#FF6B00' : '#ccc',
                        '&:hover': { color: '#FF6B00' } }}>
                      {isBookmarked ? <StarIcon /> : <StarBorderIcon />}
                    </IconButton>
                  </Tooltip>

                  {/* Titel + chips – klickbar */}
                  <Box sx={{ flex: 1, cursor: 'pointer' }}
                    onClick={() => setExpandedId(expanded ? null : item.id)}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                      {matching.length > 0 && (
                        <Tooltip title={`Larm: ${matching.map(a => a.name).join(', ')}`}>
                          <NotificationsActiveIcon sx={{ fontSize: 15, color: '#1565C0' }} />
                        </Tooltip>
                      )}
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1.3 }}>
                        {item.title}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {item.role && <Chip size="small" label={item.role} sx={{ fontSize: '0.7rem', height: 20 }} />}
                      {item.competenceArea && (
                        <Chip size="small" label={item.competenceArea}
                          sx={{ fontSize: '0.7rem', height: 20, bgcolor: '#e3f2fd', color: '#1565C0' }} />
                      )}
                      {item.workPlace && <Chip size="small" label={item.workPlace} sx={{ fontSize: '0.7rem', height: 20 }} />}
                      {item.scope && <Chip size="small" label={item.scope} sx={{ fontSize: '0.7rem', height: 20 }} />}
                    </Box>
                  </Box>

                  {/* Höger: status + datum */}
                  <Box sx={{ textAlign: 'right', flexShrink: 0, cursor: 'pointer' }}
                    onClick={() => setExpandedId(expanded ? null : item.id)}>
                    {st.label && (
                      <Chip size="small" label={st.label}
                        sx={{ bgcolor: st.bg, color: st.color, fontWeight: 600, fontSize: '0.7rem', mb: 0.5 }} />
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.3 }}>
                      {item.city && (
                        <Typography variant="caption" color="text.secondary">📍 {item.city}</Typography>
                      )}
                      {item.lastResponseDate && (
                        <Typography variant="caption"
                          sx={{ color: days !== null && days <= 3 ? '#c62828' : days !== null && days <= 7 ? '#e65100' : 'text.secondary',
                            fontWeight: days !== null && days <= 7 ? 600 : 400 }}>
                          Svar senast {item.lastResponseDate}
                          {days !== null && days >= 0 && ` (${days} d)`}
                          {days !== null && days < 0 && ' (utgått)'}
                        </Typography>
                      )}
                      {item.startDate && (
                        <Typography variant="caption" color="text.secondary">Start {item.startDate}</Typography>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* Expanderat innehåll */}
                {expanded && (
                  <Box sx={{ borderTop: '1px solid #f0f0f0' }}>
                    {/* Tvåkolumnslayout: metadata till vänster, beskrivning + krav till höger */}
                    <Box sx={{ display: 'flex', gap: 0, flexWrap: 'wrap' }}>

                      {/* Vänsterkolumn: strukturerade metadata */}
                      <Box sx={{
                        flex: '0 0 220px', p: 2.5,
                        bgcolor: '#fafafa', borderRight: { sm: '1px solid #f0f0f0' },
                        display: 'flex', flexDirection: 'column', gap: 0.25,
                      }}>
                        <Typography variant="caption" fontWeight="bold" color="text.secondary"
                          sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem', display: 'block', mb: 1 }}>
                          Detaljer
                        </Typography>
                        {item.referenceNumber && (
                          <MetaRow label="Referensnummer" value={item.referenceNumber} />
                        )}
                        <MetaRow label="Kompetensområde" value={item.competenceArea} />
                        <MetaRow label="Roll" value={item.role} />
                        <MetaRow label="Omfattning" value={item.scope} />
                        <MetaRow label="Geografisk placering"
                          value={[item.city, item.country].filter(Boolean).join(', ')} />
                        <MetaRow label="Startdatum" value={formatDate(item.startDate)} icon={<CalendarTodayIcon />} />
                        <MetaRow label="Slutdatum" value={formatDate(item.endDate)} icon={<CalendarTodayIcon />} />
                        {item.lastResponseDate && (
                          <MetaRow label="Ansök senast"
                            value={formatDate(item.lastResponseDate)}
                            icon={<CalendarTodayIcon />} />
                        )}

                        {item.contact && (
                          <>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="caption" fontWeight="bold" color="text.secondary"
                              sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
                              Konsultförmedlare
                            </Typography>
                            {item.contact.name  && <MetaRow label="Namn"   value={item.contact.name}  icon={<PersonIcon />} />}
                            {item.contact.phone && <MetaRow label="Telefon" value={item.contact.phone} icon={<PhoneIcon />} />}
                            {item.contact.email && (
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', py: 0.5 }}>
                                <EmailIcon sx={{ fontSize: 14, color: '#999', mt: '3px', flexShrink: 0 }} />
                                <Box>
                                  <Typography variant="caption" sx={{ color: '#aaa', display: 'block', lineHeight: 1.2, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.4 }}>E-post</Typography>
                                  <Typography variant="body2" component="a" href={`mailto:${item.contact.email}`}
                                    sx={{ fontSize: '0.8rem', color: '#1565C0', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                                    {item.contact.email}
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                          </>
                        )}
                      </Box>

                      {/* Högerkolumn: beskrivning + krav */}
                      <Box sx={{ flex: '1 1 380px', p: 2.5 }}>
                        {item.missionInfo && (
                          <Box sx={{ mb: 2.5 }}>
                            <Typography variant="caption" fontWeight="bold" color="text.secondary"
                              sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.75, fontSize: '0.65rem' }}>
                              Uppdragsbeskrivning
                            </Typography>
                            {stripHtml(item.missionInfo).split('\n').filter(Boolean).map((para, i) => (
                              <Typography key={i} variant="body2" color="text.secondary"
                                sx={{ lineHeight: 1.7, fontSize: '0.82rem', mb: 0.75 }}>
                                {para}
                              </Typography>
                            ))}
                          </Box>
                        )}
                        {mandatory.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" fontWeight="bold"
                              sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.75, fontSize: '0.65rem', color: '#c62828' }}>
                              Skakrav
                            </Typography>
                            {mandatory.map((r, i) => (
                              <Box key={i} sx={{ display: 'flex', gap: 1, mb: 0.5, alignItems: 'flex-start' }}>
                                <CheckCircleOutlineIcon sx={{ fontSize: 13, color: '#c62828', mt: '3px', flexShrink: 0 }} />
                                <Typography variant="caption" sx={{ fontSize: '0.78rem', lineHeight: 1.5 }}>{r.name}</Typography>
                              </Box>
                            ))}
                          </Box>
                        )}
                        {shouldHave.length > 0 && (
                          <Box>
                            <Typography variant="caption" fontWeight="bold"
                              sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.75, fontSize: '0.65rem', color: '#e65100' }}>
                              Meriterande krav
                            </Typography>
                            {shouldHave.map((r, i) => (
                              <Box key={i} sx={{ display: 'flex', gap: 1, mb: 0.5, alignItems: 'flex-start' }}>
                                <CheckCircleOutlineIcon sx={{ fontSize: 13, color: '#e65100', mt: '3px', flexShrink: 0 }} />
                                <Typography variant="caption" sx={{ fontSize: '0.78rem', lineHeight: 1.5 }}>{r.name}</Typography>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Box>

                    {/* Åtgärdsrad */}
                    <Box sx={{
                      px: 2.5, py: 1.5,
                      borderTop: '1px solid #f0f0f0',
                      display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap',
                      bgcolor: '#fafafa',
                    }}>
                      <Button size="small"
                        onClick={() => toggleBookmark(item)}
                        startIcon={isBookmarked ? <StarIcon /> : <StarBorderIcon />}
                        variant={isBookmarked ? 'contained' : 'outlined'}
                        sx={{ fontSize: '0.75rem', textTransform: 'none',
                          ...(isBookmarked
                            ? { bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }
                            : { borderColor: '#ccc', color: 'text.secondary' }
                          )}}>
                        {isBookmarked ? 'Intressant' : 'Markera som intressant'}
                      </Button>

                      <Button size="small"
                        onClick={() => toggleDismiss(item)}
                        variant={isDismissed ? 'contained' : 'outlined'}
                        sx={{ fontSize: '0.75rem', textTransform: 'none',
                          ...(isDismissed
                            ? { bgcolor: '#757575', color: '#fff', '&:hover': { bgcolor: '#616161' } }
                            : { borderColor: '#ccc', color: 'text.secondary' }
                          )}}>
                        {isDismissed ? 'Ointressant' : 'Markera som ointressant'}
                      </Button>

                      <Button size="small" variant="outlined"
                        startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                        onClick={() => copyShareText(item)}
                        sx={{ fontSize: '0.75rem', textTransform: 'none', borderColor: '#ccc', color: 'text.secondary' }}>
                        Kopiera sammanfattning
                      </Button>

                      <Button size="small" variant="outlined" endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                        href={item.url} target="_blank" rel="noopener noreferrer"
                        sx={{ fontSize: '0.75rem', textTransform: 'none', borderColor: '#ccc', color: 'text.secondary' }}>
                        Öppna i portal
                      </Button>

                      <Button size="small" variant="outlined"
                        startIcon={savingShare && shareDialog?.item?.id !== item.id
                          ? <CircularProgress size={12} />
                          : <ShareIcon sx={{ fontSize: 15 }} />}
                        onClick={() => openShare(item)}
                        sx={{ fontSize: '0.75rem', textTransform: 'none', borderColor: '#ccc', color: 'text.secondary' }}>
                        Dela
                      </Button>

                      <Button size="small" variant="contained"
                        startIcon={creatingTender === item.id
                          ? <CircularProgress size={12} color="inherit" />
                          : <GavelIcon sx={{ fontSize: 15 }} />}
                        disabled={creatingTender === item.id}
                        onClick={() => createTender(item)}
                        sx={{ ml: 'auto', fontSize: '0.75rem', textTransform: 'none',
                          bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' } }}>
                        Skapa anbud
                      </Button>
                    </Box>
                  </Box>
                )}
              </Paper>
            );
          })
        )}
      </Box>

      {/* ── Larmlistad dialog ── */}
      <Dialog open={alertDialog === 'list'} onClose={() => setAlertDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            Larmregler
            <Button size="small" startIcon={<AddIcon />} variant="outlined"
              onClick={() => { setAlertDialog(false); setTimeout(openNewAlert, 100); }}>
              Ny regel
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {alerts.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              Inga larmregler ännu.
            </Typography>
          ) : alerts.map(a => (
            <Box key={a.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5,
              borderBottom: '1px solid #f0f0f0' }}>
              <NotificationsActiveIcon sx={{ color: a.active !== false ? '#FF6B00' : '#bbb', fontSize: 20 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight="bold">{a.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {[
                    a.keywords?.length && `Nyckelord: ${a.keywords.join(', ')}`,
                    a.cities?.length && `Orter: ${a.cities.join(', ')}`,
                    a.departmentIds?.length && `${a.departmentIds.length} affärsområde(n)`,
                  ].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
              <Switch size="small" checked={a.active !== false} onChange={() => toggleAlert(a)} />
              <IconButton size="small" onClick={() => { setAlertDialog(false); setTimeout(() => openEditAlert(a), 100); }}>
                <SearchIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton size="small" onClick={() => deleteAlert(a.id)}>
                <DeleteIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlertDialog(false)}>Stäng</Button>
        </DialogActions>
      </Dialog>

      {/* ── Dela & kommentera-dialog ── */}
      <Dialog open={!!shareDialog} onClose={closeShare} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <ShareIcon sx={{ fontSize: 18, color: '#FF6B00' }} />
          <Box sx={{ flex: 1 }}>
            <Typography fontWeight={700} sx={{ lineHeight: 1.2 }}>
              {shareDialog?.item?.title || 'Dela upphandling'}
            </Typography>
            {shareDialog?.item?.role && (
              <Typography variant="caption" color="text.secondary">{shareDialog.item.role}</Typography>
            )}
          </Box>
          <IconButton size="small" onClick={closeShare}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {shareDialog && (() => {
            const shareUrl = `${window.location.origin}/upphandlingar?share=${shareDialog.savedId}`;
            return (
              <>
                {/* Dela-alternativ */}
                <Box sx={{ p: 2.5 }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1.5, fontSize: '0.65rem' }}>
                    Dela länk
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <TextField
                      size="small" fullWidth value={shareUrl}
                      InputProps={{ readOnly: true, sx: { fontSize: '0.78rem', bgcolor: '#f8f8f8' } }}
                    />
                    <Button variant="outlined" size="small" startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                      onClick={() => { navigator.clipboard.writeText(shareUrl); setSnackbar('Länk kopierad!'); }}
                      sx={{ textTransform: 'none', whiteSpace: 'nowrap', borderColor: '#ccc' }}>
                      Kopiera
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                    <Box sx={{ p: 1, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fff', flexShrink: 0 }}>
                      <QRCodeSVG value={shareUrl} size={88} />
                    </Box>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {shareDialog.item.url && (
                        <Button size="small" variant="outlined"
                          endIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
                          href={shareDialog.item.url} target="_blank" rel="noopener noreferrer"
                          sx={{ textTransform: 'none', borderColor: '#ccc', color: 'text.secondary', justifyContent: 'flex-start' }}>
                          Öppna i Upgraded People
                        </Button>
                      )}
                    </Box>
                  </Box>

                  {/* Bjud in kollegor */}
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary"
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1, fontSize: '0.65rem' }}>
                      Bjud in kollegor
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <Autocomplete
                        multiple size="small" fullWidth
                        options={allUsers}
                        value={selectedInvitees}
                        onChange={(_, v) => setSelectedInvitees(v)}
                        getOptionLabel={u => u.name || u.email}
                        isOptionEqualToValue={(a, b) => a.uid === b.uid}
                        renderInput={params => (
                          <TextField {...params} placeholder="Välj kollegor…"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.85rem' } }} />
                        )}
                        renderOption={(props, u) => (
                          <Box component="li" {...props} sx={{ gap: 1 }}>
                            <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem', bgcolor: '#FF6B00', flexShrink: 0 }}>
                              {(u.name || u.email).charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>{u.name}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>{u.email}</Typography>
                            </Box>
                          </Box>
                        )}
                      />
                      <Button
                        variant="contained" size="small"
                        disabled={!selectedInvitees.length || sendingInvite}
                        onClick={sendInvite}
                        startIcon={sendingInvite ? <CircularProgress size={12} color="inherit" /> : <SendIcon sx={{ fontSize: 14 }} />}
                        sx={{ whiteSpace: 'nowrap', textTransform: 'none', bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' }, '&:disabled': { bgcolor: '#f0c0a0' } }}>
                        Skicka
                      </Button>
                    </Box>
                  </Box>
                </Box>

                {/* Delarens meddelande */}
                <Box sx={{ px: 2.5, pb: 2 }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1, fontSize: '0.65rem' }}>
                    Ditt meddelande (bifogas i notifieringar)
                  </Typography>
                  <TextField
                    fullWidth multiline rows={2} size="small"
                    placeholder="Skriv ett meddelande till dina kollegor om varför du delar detta…"
                    value={shareNote}
                    onChange={e => setShareNote(e.target.value)}
                    onBlur={() => shareDialog && saveShareNote(shareDialog.savedId, shareNote)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.85rem' } }}
                  />
                </Box>

                <Divider />

                {/* Kommentarstråd */}
                <Box sx={{ p: 2.5 }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1.5, fontSize: '0.65rem' }}>
                    Kommentarer ({savedComments.length})
                  </Typography>

                  {savedComments.length === 0 ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                      Inga kommentarer ännu. Var den första att kommentera.
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2, maxHeight: 300, overflowY: 'auto' }}>
                      {savedComments.map(c => (
                        <Box key={c.id} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                          <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: '#FF6B00', flexShrink: 0 }}>
                            {c.authorInitial || c.authorName?.charAt(0) || '?'}
                          </Avatar>
                          <Box sx={{ flex: 1, bgcolor: '#f8f8f8', borderRadius: 2, px: 1.5, py: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                              <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.75rem' }}>
                                {c.authorName}
                              </Typography>
                              {c.createdAt?.toDate && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                                  {c.createdAt.toDate().toLocaleString('sv-SE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                </Typography>
                              )}
                            </Box>
                            <Typography variant="body2" sx={{ fontSize: '0.82rem', lineHeight: 1.5 }}>
                              {c.text}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Ny kommentar */}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                    <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: '#1a1a1a', flexShrink: 0, mb: 0.5 }}>
                      {(userProfile?.name || currentUser?.displayName || '?').charAt(0).toUpperCase()}
                    </Avatar>
                    <TextField
                      fullWidth multiline maxRows={4} size="small"
                      placeholder="Skriv en kommentar…"
                      value={newCommentText}
                      onChange={e => setNewCommentText(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitComment(); } }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.85rem' } }}
                    />
                    <IconButton
                      size="small" onClick={submitComment}
                      disabled={!newCommentText.trim() || sendingComment}
                      sx={{ mb: 0.5, color: '#FF6B00', '&:disabled': { color: '#ccc' } }}>
                      {sendingComment ? <CircularProgress size={16} /> : <SendIcon sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </Box>
                </Box>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={!!snackbar} autoHideDuration={3000} onClose={() => setSnackbar('')}
        message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* ── Ny/redigera larm dialog ── */}
      <Dialog open={alertDialog === true} onClose={() => setAlertDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editAlertId ? 'Redigera larmregel' : 'Ny larmregel'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
            <TextField fullWidth size="small" label="Regelnamn *"
              placeholder="t.ex. IT-uppdrag Stockholm"
              value={alertForm.name} onChange={e => setAlertForm(f => ({ ...f, name: e.target.value }))} />
            <TextField fullWidth size="small" label="Nyckelord (kommaseparerade)"
              placeholder="t.ex. systemförvaltning, projektledning, IT"
              value={alertForm.keywords} onChange={e => setAlertForm(f => ({ ...f, keywords: e.target.value }))}
              helperText="Matchar mot titel, roll, beskrivning och krav" />
            <TextField fullWidth size="small" label="Orter (kommaseparerade)"
              placeholder="t.ex. Stockholm, Solna"
              value={alertForm.cities} onChange={e => setAlertForm(f => ({ ...f, cities: e.target.value }))} />
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight="bold"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
                Koppla till affärsområde(n)
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {departments.map(d => {
                  const sel = alertForm.departmentIds.includes(d.id);
                  return (
                    <Chip key={d.id} size="small" label={deptLabel(d)}
                      onClick={() => setAlertForm(f => ({
                        ...f,
                        departmentIds: sel
                          ? f.departmentIds.filter(id => id !== d.id)
                          : [...f.departmentIds, d.id],
                      }))}
                      variant={sel ? 'filled' : 'outlined'}
                      sx={{ cursor: 'pointer',
                        bgcolor: sel ? '#1a1a1a' : 'transparent',
                        color: sel ? 'white' : 'text.primary' }} />
                  );
                })}
              </Box>
            </Box>
            <FormControlLabel
              control={<Switch checked={alertForm.active}
                onChange={e => setAlertForm(f => ({ ...f, active: e.target.checked }))} />}
              label="Aktiv" />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAlertDialog(false)}>Avbryt</Button>
          <Button variant="contained" disabled={!alertForm.name.trim()} onClick={saveAlert}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
            Spara
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
